import { useCallback, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getUniId } from '@/core/utils/extractCookieId';
import './Pagination.scss';

const BdsIcon = 'bds-icon' as any;

export interface PaginationProps {
  totalItems?: number;
  initialPage?: number;
  initialLimit?: number;
  eventName?: string;
  storageKeyBase?: string;
}

export const Pagination = ({
  totalItems = 0,
  initialPage = 1,
  initialLimit = 20,
  eventName = 'mfe-pagination-change',
  storageKeyBase = 'MFE_PROPS_POSTS_LIMIT',
}: PaginationProps) => {
  const { t, i18n } = useTranslation();

  const getInitialLimit = () => {
    try {
      const uni_id = getUniId();
      const storedLimit = localStorage.getItem(`${storageKeyBase}_${uni_id}`);
      if (storedLimit) {
        const parsedLimit = parseInt(storedLimit, 10);
        if (!isNaN(parsedLimit) && parsedLimit > 0) return parsedLimit;
      }
    } catch {
      // ignore empty
    }
    return initialLimit;
  };

  const [itemsPerPage, setItemsPerPage] = useState(() => getInitialLimit());
  const [currentPage, setCurrentPage] = useState(initialPage);

  const [isPageDropdownOpen, setIsPageDropdownOpen] = useState(false);
  const [isLimitDropdownOpen, setIsLimitDropdownOpen] = useState(false);

  const pageDropdownRef = useRef<HTMLDivElement>(null);
  const limitDropdownRef = useRef<HTMLDivElement>(null);

  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const lowerLimit = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const upperLimit = Math.min(currentPage * itemsPerPage, totalItems);

  const hasMountedRef = useRef(false);

  useEffect(() => {
    setCurrentPage(initialPage);
  }, [initialPage]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    setItemsPerPage(initialLimit);
  }, [initialLimit]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pageDropdownRef.current && !pageDropdownRef.current.contains(event.target as Node)) {
        setIsPageDropdownOpen(false);
      }
      if (limitDropdownRef.current && !limitDropdownRef.current.contains(event.target as Node)) {
        setIsLimitDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const dispatchPaginationEvent = useCallback((page: number, limit: number) => {
    window.dispatchEvent(
      new CustomEvent(eventName, {
        detail: { page, offset: (page - 1) * limit, limit },
        bubbles: true,
        composed: true,
      })
    );
  }, [eventName]);

  const changePage = useCallback((targetPage: number) => {
    if (targetPage < 1 || targetPage > totalPages) return;
    setCurrentPage(targetPage);
    dispatchPaginationEvent(targetPage, itemsPerPage);
    setIsPageDropdownOpen(false);
  }, [totalPages, itemsPerPage, dispatchPaginationEvent]);

  const handleLimitSelect = useCallback((newLimit: number) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1);
    dispatchPaginationEvent(1, newLimit);
    setIsLimitDropdownOpen(false);
  }, [dispatchPaginationEvent]);

  const pageOptions = Array.from({ length: totalPages }, (_, i) => i + 1);
  const perPageOptions = [10, 20, 40, 60, 100];

  return (
    <div className="pagination">
      <button
        type="button"
        className="pagination__nav-btn"
        disabled={currentPage <= 1}
        onClick={() => changePage(currentPage - 1)}
      >
        <BdsIcon name="arrow-left" theme="outline" />
        {t('pagination.prev')}
      </button>

      <div className="pagination__middle">
        <div className="pagination__control">
          <span className="pagination__label">{t('pagination.page')}</span>
          <div className="pagination__custom-select" ref={pageDropdownRef}>
            <button
              type="button"
              className="pagination__select-btn"
              onClick={() => setIsPageDropdownOpen(!isPageDropdownOpen)}
            >
              <span>{currentPage}</span>
              <BdsIcon name="arrow-down" theme="outline" class="pagination__select-icon" />
            </button>

            {isPageDropdownOpen && (
              <ul className="pagination__dropdown">
                {pageOptions.map(page => (
                  <li key={page}>
                    <button
                      type="button"
                      className={page === currentPage ? 'active' : ''}
                      onClick={() => changePage(page)}
                    >
                      {page}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="pagination__info">
          {t('pagination.showing')} <span>{lowerLimit}</span> {t('pagination.to')}{' '}
          <span>{upperLimit}</span> ({t('pagination.total')}:{' '}
          <span>{totalItems.toLocaleString(i18n.language)}</span>)
        </div>

        <div className="pagination__control">
          <span className="pagination__label">{t('pagination.perPage')}</span>
          <div className="pagination__custom-select" ref={limitDropdownRef}>
            <button
              type="button"
              className="pagination__select-btn"
              onClick={() => setIsLimitDropdownOpen(!isLimitDropdownOpen)}
            >
              <span>{itemsPerPage}</span>
              <BdsIcon name="arrow-down" theme="outline" class="pagination__select-icon" />
            </button>

            {isLimitDropdownOpen && (
              <ul className="pagination__dropdown pagination__dropdown--no-scroll">
                {perPageOptions.map(opt => (
                  <li key={opt}>
                    <button
                      type="button"
                      className={opt === itemsPerPage ? 'active' : ''}
                      onClick={() => handleLimitSelect(opt)}
                    >
                      {opt}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <button
        type="button"
        className="pagination__nav-btn"
        disabled={currentPage >= totalPages}
        onClick={() => changePage(currentPage + 1)}
      >
        {t('pagination.next')}
        <BdsIcon name="arrow-right" theme="outline" />
      </button>
    </div>
  );
};
